import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Grid, Divider, Box, Button, Tooltip, CircularProgress } from "@material-ui/core";
import Pagination from "@material-ui/lab/Pagination";
import clsx from "clsx";
import SearchService from "../../services/SearchService";
import OneParkService from "../../services/OneParkService";
import apiClient from "../../auth/apiClient";
import useCurrentFacility from "../../hooks/useCurrentFacility";
import { useEnqueueSnackbar } from "../../hooks/useEnqueueSnackbar";
import { useSelector } from "react-redux";

import OneParkInviteSearchButton from "./OneParkInviteSearchButton";
import OneParkInviteTable from "./OneParkInviteTable";
import { useStyles } from "./styles";
import useCurrentFacilityTimezone from "../../hooks/useCurrentFacilityTimezone";

const ITEM_LIMIT = 10;

const searchService = new SearchService(apiClient);
const oneParkService = new OneParkService(apiClient);

const descendingComparator = (a, b, orderBy) => {
  if (orderBy === "lastUse") {
    const aTime = a.lastUseRaw ? new Date(a.lastUseRaw).getTime() : 0;
    const bTime = b.lastUseRaw ? new Date(b.lastUseRaw).getTime() : 0;
    if (aTime === 0 && bTime === 0) return 0;
    if (bTime === 0) return -1;
    if (aTime === 0) return 1;
    return bTime - aTime;
  }
  if (!b[orderBy]) return -1;
  if (!a[orderBy]) return 1;
  return b[orderBy]
    .toString()
    .toLowerCase()
    .localeCompare(a[orderBy].toString().toLowerCase());
};

const getComparator = (order, orderBy) =>
  order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);

const OneParkInviteContainer = () => {
  const classes = useStyles();
  const { facilityID } = useCurrentFacility();
  const { convertUtcDateToFormat } = useCurrentFacilityTimezone();
  const enqueueSnackbar = useEnqueueSnackbar();

  const scopeAwareFacilityID = useSelector(
    (state) => state.entityScope?.facilityGroupId || facilityID
  );

  const [activeSearch, setActiveSearch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  const [orderBy, setOrderBy] = useState("firstName");
  const [order, setOrder] = useState("asc");
  const [page, setPage] = useState(1);
  const [sending, setSending] = useState(false);
  const [anyResultHasEmail, setAnyResultHasEmail] = useState(false);

  const fetchInviteResults = useCallback(async () => {
    if (!activeSearch) return;

    setLoading(true);
    const offset = (page - 1) * ITEM_LIMIT;

    try {
      const response = await searchService.searchAccessHolders(
        scopeAwareFacilityID,
        activeSearch.searchTerm,
        activeSearch.searchCategory,
        null,
        null,
        null,
        null,
        ITEM_LIMIT,
        offset,
        null,
        true
      );

      const formatted =
        response?.data?.collection?.map((r) => ({
          id: r.accessHolderID,
          firstName: r.contactInfo?.firstName ?? "",
          lastName: r.contactInfo?.lastName ?? "",
          email:
            r.contactInfo?.emails?.length > 0
              ? r.contactInfo.emails[0].emailAddress
              : "",
          lastUse: r.lastUse ? convertUtcDateToFormat(r.lastUse) : "--",
          lastUseRaw: r.lastUse,
        })) ?? [];

      setRows(formatted);
      setTotalCount(response?.data?.totalCount ?? 0);
      setAnyResultHasEmail(response?.data?.anyResultHasEmail ?? false);
    } catch {
      setRows([]);
      setTotalCount(0);
      setAnyResultHasEmail(false);
    } finally {
      setLoading(false);
    }
  }, [activeSearch, scopeAwareFacilityID, page]);

  useEffect(() => {
    fetchInviteResults();
  }, [fetchInviteResults]);

  const sortedRows = useMemo(
    () => [...rows].sort(getComparator(order, orderBy)),
    [rows, order, orderBy]
  );

  const handlePageChange = (e, value) => {
    setLoading(true);
    setPage(value);
  };

  const pageCount = Math.ceil(totalCount / ITEM_LIMIT);

  const handleSendInvites = useCallback(async () => {
    if (!activeSearch || rows.length === 0) return;

    setSending(true);
    try {
      const invitePayload = {
        searchTerm: activeSearch.searchTerm,
        searchCategory: activeSearch.searchCategory,
        scopeAwareFacilityID,
      };
      const response = await oneParkService.sendInvites(facilityID, invitePayload);
      const sentCount = response?.data?.sentCount ?? 0;
      enqueueSnackbar(`${sentCount} emails sent`, {
        variant: "success",
        tag: "OneParkInvitesSent",
      });
    } catch (err) {
      const message = err?.response?.data?.message || err?.response?.data || "Failed to send invites";
      enqueueSnackbar(String(message), {
        variant: "error",
        tag: "OneParkInvitesFailed",
      });
    } finally {
      setSending(false);
    }
  }, [activeSearch, rows, facilityID, scopeAwareFacilityID, enqueueSnackbar]);

  return (
    <Box className={clsx("pageable-entity", "access-holder-pageable-entity", "onepark-invite-container")}>
      <Grid container>
        <Grid item xs={12} md={9}>
          <OneParkInviteSearchButton
            loading={loading}
            onSearch={(search) => {
              setPage(1);
              setLoading(true);
              setRows([]);
              setTotalCount(0);
              setActiveSearch(search);
            }}
          />
        </Grid>

        <Grid item xs={12} md={3}>
        </Grid>
      </Grid>
      <Divider className={clsx(classes.sectionDivider, "onepark-invite-divider")} />
      
      {activeSearch && (
        <>
          {pageCount > 1 && (
            <div className={clsx(classes.paginationWrapper, "onepark-invite-pagination-wrapper")}>
              <Pagination
                className={clsx("onepark-invite-pagination")}
                page={page}
                count={pageCount}
                onChange={handlePageChange}
                size="small"
                shape="rounded"
                color="primary"
              />
            </div>
          )}
        </>
      )}

      {activeSearch && (
        <div className={clsx(classes.contentWrapper, "onepark-invite-content-wrapper")}>
          <div className={clsx(classes.contentInner, "onepark-invite-content-inner")}>
            {loading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress className="onepark-invite-loading-spinner" />
              </Box>
            ) : sortedRows.length > 0 ? (
              <>
                <OneParkInviteTable
                  rows={sortedRows}
                  order={order}
                  orderBy={orderBy}
                  onSort={(column) => {
                    const isAsc = orderBy === column && order === "asc";
                    setOrder(isAsc ? "desc" : "asc");
                    setOrderBy(column);
                  }}
                />
                <div className={clsx(classes.sendInvitesWrapper, "onepark-send-invites-wrapper")}>
                  <Tooltip
                    title={!anyResultHasEmail ? "No emails on file for listed access holders" : ""}
                    arrow
                  >
                    <span>
                      <Button
                        variant="contained"
                        color="primary"
                        className={clsx("onepark-send-invites-button")}
                        disabled={!anyResultHasEmail || sending}
                        onClick={handleSendInvites}
                      >
                        Send Invites To List
                      </Button>
                    </span>
                  </Tooltip>
                </div>
              </>
            ) : (
              <div className={clsx(classes.noResults, "onepark-invite-no-results")}>
                <span className={clsx("no-results-text", "onepark-invite-no-results-text")}>No results found</span>
              </div>
            )}
          </div>
        </div>
      )}
    </Box>
  );
};

export default OneParkInviteContainer;
